import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, ShoppingBag } from 'lucide-react';

// Logo image
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/ob2xdoj4_279749100_449900870272747_652948079724847386_n.jpg';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/goats', label: 'Goats & Ranch' },
  { href: '/shop', label: 'Shop' },
  { href: '/gallery', label: 'Gallery' },
  { href: '/reviews', label: 'Reviews' },
  { href: '/faq', label: 'FAQ' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-card/95 backdrop-blur-md shadow-md py-2'
          : 'bg-transparent py-3'
      )}
    >
      <div className="container mx-auto px-4 md:px-6">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img 
              src={LOGO_URL} 
              alt="Little House on the Table" 
              className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover shadow-md border-2 border-white/50 transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col">
              <span className="font-script text-lg md:text-xl text-primary leading-none">
                Little House
              </span>
              <span className="font-serif text-[10px] md:text-xs text-muted-foreground tracking-wider uppercase">
                on the Table
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'px-4 py-2 rounded-lg font-sans text-sm font-medium transition-all duration-200',
                  location.pathname === link.href
                    ? 'text-primary bg-primary/10'
                    : 'text-foreground/80 hover:text-primary hover:bg-primary/5'
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              asChild
              className="btn-beveled px-6 py-2.5 font-sans font-semibold text-sm"
            >
              <Link
                to="/shop"
                className="flex items-center gap-2"
              >
                <ShoppingBag className="h-4 w-4" />
                Shop Now
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Trigger */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-card">
              <div className="flex flex-col h-full pt-8">
                {/* Mobile Logo */}
                <Link
                  to="/"
                  className="flex items-center gap-2 mb-8"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <img 
                    src={LOGO_URL} 
                    alt="Little House on the Table" 
                    className="h-12 w-12 rounded-full object-cover shadow-md"
                  />
                  <div className="flex flex-col">
                    <span className="font-script text-lg text-primary leading-none">
                      Little House
                    </span>
                    <span className="font-serif text-[10px] text-muted-foreground tracking-wider uppercase">
                      on the Table
                    </span>
                  </div>
                </Link>

                {/* Mobile Nav Links */}
                <nav className="flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        'px-4 py-3 rounded-lg font-sans text-base font-medium transition-all',
                        location.pathname === link.href
                          ? 'text-primary bg-primary/10'
                          : 'text-foreground/80 hover:text-primary hover:bg-primary/5'
                      )}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>

                {/* Mobile CTA */}
                <div className="mt-auto pb-8">
                  <Button
                    asChild
                    className="w-full btn-beveled py-3 font-sans font-semibold"
                  >
                    <Link
                      to="/shop"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center justify-center gap-2"
                    >
                      <ShoppingBag className="h-4 w-4" />
                      Shop Now
                    </Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Header;
